import { CATEGORIES, todayStr } from "../../constants/categories";

const LEVELS = [
  { min: 0, title: "Tân Binh", icon: "🌱" },
  { min: 3, title: "Học Viên", icon: "📖" },
  { min: 7, title: "Chiến Binh", icon: "⚔️" },
  { min: 14, title: "Dũng Sĩ", icon: "🛡️" },
  { min: 30, title: "Hiệp Sĩ", icon: "🗡️" },
  { min: 50, title: "Cao Thủ", icon: "🔮" },
  { min: 75, title: "Đại Sư", icon: "🏯" },
  { min: 100, title: "Huyền Thoại", icon: "👑" },
  { min: 150, title: "Bất Tử", icon: "🐉" },
  { min: 200, title: "Thần Thánh", icon: "⭐" },
];

const ACHIEVEMENTS = [
  { id: "first", icon: "🎯", name: "Bước Đầu", desc: "Hoàn thành ngày đầu tiên", check: (s) => s.completed >= 1 },
  { id: "week", icon: "🔥", name: "Tuần Lửa", desc: "Streak 7 ngày", check: (s) => s.longestStreak >= 7 },
  { id: "two_weeks", icon: "💎", name: "Kiên Định", desc: "Streak 14 ngày", check: (s) => s.longestStreak >= 14 },
  { id: "month", icon: "🏆", name: "Bậc Thầy", desc: "Streak 30 ngày", check: (s) => s.longestStreak >= 30 },
  { id: "fifty", icon: "⚡", name: "Sấm Sét", desc: "50 ngày hoàn thành", check: (s) => s.completed >= 50 },
  { id: "hundred", icon: "👑", name: "Vương Giả", desc: "100 ngày hoàn thành", check: (s) => s.completed >= 100 },
  { id: "all_cats", icon: "🌈", name: "Toàn Diện", desc: "Hoàn thành đủ 6 lĩnh vực", check: (s) => s.categoriesCompleted >= 6 },
  { id: "streak50", icon: "🐉", name: "Rồng Lửa", desc: "Streak 50 ngày", check: (s) => s.longestStreak >= 50 },
];

const ACCENT = "#8B5CF6";
const GOLD = "#F59E0B";


function getLevel(completed) {
  let level = LEVELS[0];
  for (const l of LEVELS) {
    if (completed >= l.min) level = l;
  }
  return level;
}

function getLevelIndex(completed) {
  let idx = 0;
  for (let i = 0; i < LEVELS.length; i++) {
    if (completed >= LEVELS[i].min) idx = i;
  }
  return idx;
}

function getXPProgress(completed) {
  const idx = getLevelIndex(completed);
  const currentMin = LEVELS[idx].min;
  const nextMin = idx < LEVELS.length - 1 ? LEVELS[idx + 1].min : LEVELS[idx].min + 50;
  const progress = ((completed - currentMin) / (nextMin - currentMin)) * 100;
  return { progress: Math.min(progress, 100), current: completed - currentMin, needed: nextMin - currentMin, nextTitle: idx < LEVELS.length - 1 ? LEVELS[idx + 1].title : "MAX" };
}

function getCategoryStats(history) {
  const stats = {};
  CATEGORIES.forEach(c => { stats[c.id] = { total: 0, completed: 0 }; });
  history.forEach(h => {
    if (stats[h.category_id]) {
      stats[h.category_id].total++;
      if (h.completed) stats[h.category_id].completed++;
    }
  });
  const maxCompleted = Math.max(1, ...Object.values(stats).map(s => s.completed));
  return CATEGORIES.map(c => ({
    ...c,
    completed: stats[c.id].completed,
    total: stats[c.id].total,
    percent: (stats[c.id].completed / maxCompleted) * 100,
  }));
}

export default function WarriorStyle({
  histMap, history, completedCount, streak, completionRate,
  calMonth, calYear, monthDays, firstDayOfWeek, monthNames,
  prevMonth, nextMonth, isCurrentMonth,
  selectedDay, setSelectedDay,
}) {
  const level = getLevel(completedCount);
  const levelIdx = getLevelIndex(completedCount);
  const xp = getXPProgress(completedCount);
  const categoryStats = getCategoryStats(history);
  const categoriesCompleted = categoryStats.filter(c => c.completed > 0).length;

  const achievementStats = {
    completed: completedCount,
    longestStreak: streak.longest_streak || 0,
    categoriesCompleted,
  };

  const unlockedAchievements = ACHIEVEMENTS.filter(a => a.check(achievementStats));
  const lockedAchievements = ACHIEVEMENTS.filter(a => !a.check(achievementStats));

  // XP ring SVG params
  const ringSize = 140;
  const strokeWidth = 10;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (xp.progress / 100) * circumference;

  return (
    <>
      {/* Character Card */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "20px",
        padding: "28px 20px",
        marginBottom: "20px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        boxShadow: "var(--card-shadow)",
      }}>
        {/* Decorative glow */}
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 20%, ${ACCENT}08, transparent 60%)`, pointerEvents: "none" }} />

        {/* XP Ring */}
        <div style={{ position: "relative", display: "inline-block", marginBottom: "12px" }}>
          <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={ringSize / 2} cy={ringSize / 2} r={radius}
              fill="none" stroke="var(--bg-inset)" strokeWidth={strokeWidth} />
            <circle cx={ringSize / 2} cy={ringSize / 2} r={radius}
              fill="none" stroke={`url(#xpGradient)`} strokeWidth={strokeWidth}
              strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 1s ease" }} />
            <defs>
              <linearGradient id="xpGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={ACCENT} />
                <stop offset="100%" stopColor={GOLD} />
              </linearGradient>
            </defs>
          </svg>
          <div style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontSize: "36px" }}>{level.icon}</span>
            <span style={{ fontSize: "11px", color: ACCENT, fontWeight: "700", marginTop: "2px" }}>Lv.{levelIdx + 1}</span>
          </div>
        </div>

        {/* Title */}
        <div style={{ fontSize: "22px", fontWeight: "900", color: "#D97706", letterSpacing: "1px" }}>
          {level.title}
        </div>
        <div style={{ fontSize: "12px", color: "var(--text-tertiary)", marginTop: "4px" }}>
          {xp.nextTitle !== "MAX" ? `${xp.current}/${xp.needed} XP đến ${xp.nextTitle}` : "Đã đạt cấp tối đa!"}
        </div>

        {/* Quick Stats */}
        <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginTop: "16px" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "20px", fontWeight: "900", color: ACCENT }}>{completedCount}</div>
            <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Nhiệm vụ</div>
          </div>
          <div style={{ width: "1px", background: "var(--border-secondary)" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "20px", fontWeight: "900", color: "#D97706" }}>{streak.current_streak || 0}🔥</div>
            <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Streak</div>
          </div>
          <div style={{ width: "1px", background: "var(--border-secondary)" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "20px", fontWeight: "900", color: "#16A34A" }}>{streak.longest_streak || 0}🏆</div>
            <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Kỷ lục</div>
          </div>
        </div>
      </div>

      {/* Category Power Stats */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>⚔️</span> Chỉ Số Sức Mạnh
        </div>
        {categoryStats.map(cat => (
          <div key={cat.id} style={{ marginBottom: "10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
              <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                {cat.emoji} {cat.label}
              </span>
              <span style={{ fontSize: "11px", color: cat.color, fontWeight: "700" }}>
                {cat.completed}
              </span>
            </div>
            <div style={{ background: "var(--border-secondary)", borderRadius: "6px", height: "8px", overflow: "hidden" }}>
              <div style={{
                height: "100%",
                borderRadius: "6px",
                background: `linear-gradient(90deg, ${cat.color}, ${cat.color}AA)`,
                width: `${cat.percent}%`,
                transition: "width 0.8s ease",
                boxShadow: cat.completed > 0 ? `0 0 8px ${cat.color}44` : "none",
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Achievements */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
        boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>🏅</span> Thành Tựu ({unlockedAchievements.length}/{ACHIEVEMENTS.length})
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
          {unlockedAchievements.map(a => (
            <div key={a.id} style={{
              textAlign: "center",
              padding: "10px 4px",
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${GOLD}15, ${ACCENT}10)`,
              border: `1px solid ${GOLD}33`,
            }}>
              <div style={{ fontSize: "24px", marginBottom: "4px" }}>{a.icon}</div>
              <div style={{ fontSize: "9px", fontWeight: "700", color: "#D97706", lineHeight: 1.2 }}>{a.name}</div>
              <div style={{ fontSize: "8px", color: "var(--text-muted)", marginTop: "2px", lineHeight: 1.2 }}>{a.desc}</div>
            </div>
          ))}
          {lockedAchievements.map(a => (
            <div key={a.id} style={{
              textAlign: "center",
              padding: "10px 4px",
              borderRadius: "12px",
              background: "var(--bg-inset)",
              border: "1px solid var(--border-secondary)",
            }}>
              <div style={{ fontSize: "24px", marginBottom: "4px", filter: "grayscale(1)", opacity: 0.5 }}>🔒</div>
              <div style={{ fontSize: "9px", fontWeight: "700", color: "var(--text-muted)", lineHeight: 1.2 }}>{a.name}</div>
              <div style={{ fontSize: "8px", color: "var(--text-tertiary)", marginTop: "2px", lineHeight: 1.2 }}>{a.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Quest Log */}
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-secondary)",
        borderRadius: "16px",
        padding: "20px",
        marginBottom: "20px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <button onClick={prevMonth} style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", fontSize: "20px", padding: "4px 10px" }}>‹</button>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>{monthNames[calMonth]} {calYear}</div>
          <button onClick={nextMonth} style={{ background: "none", border: "none", color: isCurrentMonth ? "var(--border-primary)" : "var(--text-tertiary)", cursor: isCurrentMonth ? "default" : "pointer", fontSize: "20px", padding: "4px 10px" }}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "6px", textAlign: "center" }}>
          {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map(d => (
            <div key={d} style={{ fontSize: "11px", color: "var(--text-muted)", padding: "6px 0", fontWeight: "700" }}>{d}</div>
          ))}
          {Array.from({ length: (firstDayOfWeek + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: monthDays }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const items = histMap[dateStr] || [];
            const hasCompleted = items.some(c => c.completed);
            const hasData = items.length > 0;
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const isSelected = Array.isArray(selectedDay) && selectedDay[0]?.date === dateStr;
            const cat = hasCompleted ? CATEGORIES.find(c => c.id === items.find(x => x.completed)?.category_id) : null;
            const cellColor = cat ? cat.color : ACCENT;

            return (
              <div key={day} onClick={() => !isFuture && hasData && setSelectedDay(items)}
                style={{
                  aspectRatio: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  cursor: hasData && !isFuture ? "pointer" : "default",
                  background: isSelected ? `${ACCENT}25` : hasCompleted ? `${cellColor}20` : "transparent",
                  border: isToday ? `2px solid ${ACCENT}` : isSelected ? `2px solid ${ACCENT}88` : hasCompleted ? `1.5px solid ${cellColor}44` : "1.5px solid transparent",
                  boxSizing: "border-box",
                  transition: "all 0.2s",
                  position: "relative",
                }}>
                {hasCompleted ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ fontSize: "14px" }}>{cat?.emoji || "✅"}</span>
                    <span style={{ fontSize: "9px", color: cellColor, fontWeight: "700" }}>{day}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "12px",
                    fontWeight: isToday ? "800" : "500",
                    color: isFuture ? "var(--border-primary)" : isToday ? ACCENT : hasData ? "var(--text-tertiary)" : "var(--text-muted)",
                  }}>{day}</span>
                )}
                {isToday && !hasCompleted && (
                  <div style={{
                    position: "absolute", bottom: "3px",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: ACCENT, animation: "pulse 2s infinite",
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
